import {
  DataType,
  Form,
  FormOptions,
  Section,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity, EnumUnidadMedida } from '../../../../../../api'
import { ProfileModel } from '../../../../../../api/Argentia/endpoints/profiles/model/profiles'
import { EnumNivelDesglose } from '../../../../../../api/Argentia/enums/enumNivelDesglose'
import { EnumProrrateo } from '../../../../../../api/Argentia/enums/enumProrrateo'
import { MeasureUnit } from '../../../../../../api/Pioneer/model/MeasureUnit'
import { QuantityAssistantModal } from '../../../../../../components/shared/quantityAssistantModal/quantityAssistanModal'
import { QuantityAssistantDataTableRow } from '../../../../../../components/shared/quantityAssistantModal/quantityAssistantModal.model'
import { QuantityDataTable } from '../../../../../../components/shared/quantityDataTable/quantityDataTable'
import { QuantityDataTableRow } from '../../../../../../components/shared/quantityDataTable/quantityDataTable.model'
import { StandardCard } from '../../../../../../components/shared/standardCard/standardCard'
import {
  enumToSelectOptionArray,
  enumToSelectOptionArrayDefaultValues
} from '../../../../../../helpers'
import { useDropdownOptions } from '../../../../../../hooks/useDropdownOptions'
import { getCustomerDomain } from '../../../../../../redux'
import { measureUnitToSelectOptions } from '../../../../../flexibility/flexibility.mapper'
import { fetchMeasureUnitPowerType } from '../../../../../flexibility/flexibility.service'
import { fetchWeightedTypeListByBreakdownType } from '../../../../../products/products.service'
import { profileModelToSelectOptions } from '../../../../../profiles/profiles.mapper'
import { fetchProfilesByLeadType } from '../../../../../profiles/profiles.services'

interface ProfileQuantityCardProps {
  data: QuantityAssistantDataTableRow[]
  isLoading?: boolean
  userPermissions: UserPermission[]
  onChangeDataTable: (newTableData: QuantityAssistantDataTableRow[]) => void
  onFormDataChange: (formData: ProfileQuantityFormModel, formSections: Section[]) => void
  commodity: EnumCommodity
  breakdownLevelId?: number
  weightedTypeId?: number
  profileId?: number
}

export interface ProfileQuantityFormModel {
  breakdownLevel?: EnumNivelDesglose
  weightedType?: EnumProrrateo
  profileId?: number
  unitMeasure?: EnumUnidadMedida
}

export const ProfileQuantityCard: React.FC<ProfileQuantityCardProps> = ({
  data,
  isLoading,
  onChangeDataTable,
  onFormDataChange,
  breakdownLevelId,
  weightedTypeId,
  profileId,
  userPermissions,
  commodity
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'leads'])
  const { error } = useNotify()
  const measureUnitDropdownOption = useDropdownOptions<MeasureUnit, []>(fetchMeasureUnitPowerType)
  const weightedTypeDropdownOption = useDropdownOptions<EnumNivelDesglose, [number]>(
    fetchWeightedTypeListByBreakdownType
  )
  const profileDropdownOption = useDropdownOptions<ProfileModel, [number, number]>(
    fetchProfilesByLeadType
  )

  const [tableData, setTableData] = useState<QuantityAssistantDataTableRow[]>(data ?? [])
  const [formData, setFormData] = useState<ProfileQuantityFormModel>({
    profileId: profileId,
    breakdownLevel: breakdownLevelId,
    weightedType: weightedTypeId
  })
  const [openAssistantModal, setOpenAssistantModal] = useState<boolean>(false)
  const noData = data && data.length === 0 && tableData && tableData.length === 0

  // #region Handlers Starts

  const handleAddProfileQuantity = async () => {
    setOpenAssistantModal(true)
  }

  // ---------------------- QuantityDataTable Handlers ----------------------- //

  const handleDataTableChange = (newTableData: QuantityDataTableRow[]) => {
    onChangeDataTable(newTableData as QuantityAssistantDataTableRow[])
    setTableData(newTableData as QuantityAssistantDataTableRow[])
  }

  // ---------------------- QuantityAssistantModal Handlers ----------------------- //
  const handleQuantityAssistantModalClose = (assistantData?: QuantityAssistantDataTableRow[]) => {
    if (assistantData) {
      const newTableData = [...tableData, ...assistantData]
      setTableData(newTableData)
      onChangeDataTable(newTableData)
    }
    setOpenAssistantModal(false)
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  const options: FormOptions = {
    elevation: 0,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('leads:components.profileQuantityCard.form.analytics')
    }
  }

  const formSections: Section[] = [
    {
      title: '',
      inputs: [
        {
          name: 'breakdownLevel',
          label: t('leads:components.profileQuantityCard.form.inputs.breakdownLevel'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumNivelDesglose, 'enums:EnumNivelDesglose'),
          inputSize: 3,
          required: true,
          onChange: (value: number | string, formState: any) => {
            const breakdownTypeId = +value
            onFormDataChange(
              {
                ...formState,
                breakdownLevel: breakdownTypeId,
                weightedType: undefined,
                profileId: undefined
              },
              formSections
            )
            weightedTypeDropdownOption.fetchData(breakdownTypeId)
            profileDropdownOption.fetchData(commodity, breakdownTypeId)
          }
        },
        {
          name: 'weightedType',
          label: t('leads:components.profileQuantityCard.form.inputs.weightedType'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArrayDefaultValues(
            EnumProrrateo,
            'enums:EnumProrrateo',
            weightedTypeDropdownOption.responseData
          ),
          isLoading: weightedTypeDropdownOption.isLoading,
          inputSize: 3,
          required: true,
          onChange: (value: number | string, formState: any) =>
            onFormDataChange({ ...formState, weightedType: +value }, formSections)
        },
        {
          name: 'profileId',
          label: t('leads:components.profileQuantityCard.form.inputs.profile'),
          type: DataType.SELECT,
          selectOptions: profileModelToSelectOptions(profileDropdownOption.responseData),
          isLoading: profileDropdownOption.isLoading,
          inputSize: 3,
          required: true,
          onChange: (value: number | string, formState: any) =>
            onFormDataChange({ ...formState, profileId: +value }, formSections)
        },
        {
          name: 'unitMeasure',
          label: t('leads:components.profileQuantityCard.form.inputs.unitMeasure'),
          type: DataType.SELECT,
          selectOptions: measureUnitToSelectOptions(measureUnitDropdownOption.responseData),
          isLoading: measureUnitDropdownOption.isLoading,
          inputSize: 3,
          onChange: (value: number | string, formState: any) =>
            onFormDataChange({ ...formState, unitMeasure: +value }, formSections)
        }
      ]
    }
  ]
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  useEffect(() => {
    weightedTypeDropdownOption.responseError && error(t(weightedTypeDropdownOption.responseError))
    profileDropdownOption.responseError && error(t(profileDropdownOption.responseError))
    measureUnitDropdownOption.responseError && error(t(measureUnitDropdownOption.responseError))
  }, [
    weightedTypeDropdownOption.responseError,
    profileDropdownOption.responseError,
    measureUnitDropdownOption.responseError
  ])

  useEffect(() => {
    measureUnitDropdownOption.fetchData()
  }, [])

  useEffect(() => {
    if (breakdownLevelId && commodity) {
      weightedTypeDropdownOption.fetchData(breakdownLevelId)
      profileDropdownOption.fetchData(commodity, breakdownLevelId)
    }
  }, [breakdownLevelId, commodity])

  // this is only for letting to know required field to father component from the start (on component mount), just in case user clicks Save Button after trigger Form onChange callback
  useEffect(() => {
    onFormDataChange(
      {
        breakdownLevel: breakdownLevelId,
        weightedType: weightedTypeId,
        profileId: profileId
      },
      formSections
    )
  }, [])

  // #endregion Hooks Ends

  // #region Footer Actions Starts
  // #endregion Footer Actions Ends

  return (
    <>
      <StandardCard
        title={t('leads:components.profileQuantityCard.title')}
        titleVariant={'secondary'}
        isLoading={isLoading}
        showEmptyContent={noData}
        emptyCaption={t('leads:components.profileQuantityCard.emptyCaption')}
        emptyButtonCallback={handleAddProfileQuantity}
        emptyButtonCaption={t('leads:components.profileQuantityCard.mainAction.add')}
      >
        <Form
          options={options}
          sections={formSections}
          data={formData}
          title={t('leads:components.profileQuantityCard.form.title')}
        />

        <QuantityDataTable
          tableData={tableData}
          canEdit={true}
          onAddMassiveClickHandler={handleAddProfileQuantity}
          onChangeDataTable={handleDataTableChange}
          dataTableUserPermissions={userPermissions}
        />
      </StandardCard>

      <QuantityAssistantModal
        open={openAssistantModal}
        onClose={handleQuantityAssistantModalClose}
      />
    </>
  )
}
